import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDBA } from '@/contexts/DBAContext';
import { mapsUrl } from '@/lib/formatters';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Calendar, CheckCircle, DollarSign, Users, TrendingUp, Clock, Activity, AlertCircle, ArrowRight, BriefcaseBusiness } from 'lucide-react';
import { HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';

const statusColors = {
  tentative: 'bg-purple-100 text-purple-700',
  scheduled: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-zinc-100 text-zinc-600',
};

export default function DashboardPage() {
  const { selectedDBA, getDBAName, getDBAColor } = useDBA();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [schedulingQueue, setSchedulingQueue] = useState([]);
  const [estimateQueue, setEstimateQueue] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const params = selectedDBA ? { dba_id: selectedDBA } : {};
        const { data } = await api.get('/dashboard/stats', { params });
        setStats(data);
        // Fetch scheduling queue
        const queue = await api.get('/dashboard/scheduling-queue', { params });
        setSchedulingQueue(queue.data);
        const estimates = await api.get('/dashboard/estimate-quote-queue', { params });
        setEstimateQueue(estimates.data);
      } catch {
        setError('Dashboard data could not load. Please sign in again if your session expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedDBA]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-40 rounded-[1.75rem] bg-blue-100" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 rounded-[1.5rem] bg-blue-100" />)}
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5 text-base font-semibold text-amber-900" data-testid="dashboard-load-error">{error || 'Dashboard data is unavailable.'}</div>;
  }

  const kpis = [
    { label: 'Open Leads', value: stats.open_leads, icon: FileText, color: 'text-sky-700', bg: 'bg-sky-100', ring: 'border-sky-100', link: '/leads', hint: 'needs follow-up' },
    { label: "Today's Jobs", value: stats.today_jobs, icon: Calendar, color: 'text-orange-700', bg: 'bg-orange-100', ring: 'border-orange-100', link: '/jobs', hint: 'route workload' },
    { label: 'Completed Jobs', value: stats.completed_jobs, icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-100', ring: 'border-emerald-100', link: '/jobs', hint: 'closed work' },
    { label: 'Revenue Pipeline', value: `$${(stats.revenue_pipeline || 0).toLocaleString()}`, icon: DollarSign, color: 'text-violet-700', bg: 'bg-violet-100', ring: 'border-violet-100', link: '/reports', hint: 'open value' },
    { label: 'Total Leads', value: stats.total_leads, icon: TrendingUp, color: 'text-blue-700', bg: 'bg-blue-100', ring: 'border-blue-100', link: '/leads' },
    { label: 'Won Leads', value: stats.won_leads, icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-100', ring: 'border-emerald-100', link: '/leads' },
    { label: 'Customers', value: stats.customer_count, icon: Users, color: 'text-sky-700', bg: 'bg-sky-100', ring: 'border-sky-100', link: '/customers' },
    { label: 'Team Members', value: stats.team_count, icon: Users, color: 'text-zinc-700', bg: 'bg-zinc-100', ring: 'border-zinc-100', link: '/team' },
  ];

  return (
    <div className="-mx-3 space-y-5 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0 lg:space-y-6">
      <HighVisibilityPageHeader
        eyebrow="Operations control"
        title="Field Command"
        subtitle={`${selectedDBA ? getDBAName(selectedDBA) : 'All Businesses'} · lead flow, dispatch pressure, revenue pipeline`}
        countLabel={`${stats.open_leads} open leads · ${stats.today_jobs} jobs today`}
        icon={BriefcaseBusiness}
        tone="blue"
        testId="dashboard-command-header"
        titleTestId="dashboard-title"
      />

      <div className="space-y-5 px-3 sm:px-0 lg:space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="dashboard-command-summary">
        <MiniCommandMetric label="Open Leads" value={stats.open_leads} tone="blue" />
        <MiniCommandMetric label="Today" value={stats.today_jobs} tone="orange" />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="kpi-grid">
        {kpis.map((kpi, i) => (
          <button key={kpi.label} type="button" className={`group rounded-[1.5rem] border bg-white p-4 text-left shadow-sm transition-[transform,background-color,border-color] duration-150 hover:-translate-y-0.5 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:rounded-md ${kpi.ring}`}
            onClick={() => navigate(kpi.link)} data-testid={`kpi-card-${kpi.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
            <div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="font-mono text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500 sm:text-xs">{kpi.label}</span>
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${kpi.bg}`}>
                  <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                </div>
              </div>
              <p className="font-heading text-3xl font-black tracking-tight text-zinc-950" data-testid={`kpi-${kpi.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                {kpi.value}
              </p>
              {kpi.hint && <p className="mt-1 text-xs font-bold text-zinc-500">{kpi.hint}</p>}
            </div>
          </button>
        ))}
      </div>

      {estimateQueue.count > 0 && (
        <Card className="rounded-[1.5rem] border border-amber-100 bg-white shadow-sm sm:rounded-md" data-testid="estimate-quote-queue">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-xl font-black font-heading sm:text-base sm:font-bold">
              <FileText className="w-5 h-5 text-amber-500" /> Estimates Needing Quotes
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs font-black ml-1" data-testid="estimate-quote-queue-count">{estimateQueue.count} pending</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {estimateQueue.items.slice(0, 5).map(estimate => (
                <button type="button" key={estimate.id} className="flex w-full items-center justify-between gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-left transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-orange-500 sm:rounded-sm sm:bg-white sm:p-3" onClick={() => navigate('/leads', { state: { openLeadId: estimate.lead_id } })} data-testid={`estimate-quote-queue-item-${estimate.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{estimate.lead?.contact_name || estimate.estimate_number}</p>
                    <p className="text-xs text-zinc-500">{estimate.estimate_number} · ${Number(estimate.calculation?.final_price || 0).toLocaleString()} · saved, no quote yet</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-2"><Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">needs quote</Badge><ArrowRight className="hidden h-4 w-4 text-zinc-400 sm:block" /></span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduling Queue */}
      {schedulingQueue.length > 0 && (
        <Card className="rounded-[1.5rem] border border-violet-100 bg-white shadow-sm sm:rounded-md" data-testid="scheduling-queue">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-xl font-black font-heading sm:text-base sm:font-bold">
              <AlertCircle className="w-5 h-5 text-purple-500" /> Scheduling Queue
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs font-black ml-1">{schedulingQueue.length} pending</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {schedulingQueue.slice(0, 5).map(job => (
                <button key={job.id} type="button" className="flex w-full items-center justify-between gap-3 rounded-2xl border border-violet-100 bg-violet-50 p-4 text-left transition-colors hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-orange-500 sm:rounded-sm sm:bg-white sm:p-3"
                  onClick={() => navigate('/jobs', { state: { openJobId: job.id } })} data-testid={`queue-job-${job.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-1 h-10 rounded-full" style={{ backgroundColor: getDBAColor(job.dba_id) }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{job.title}</p>
                      <p className="text-xs text-zinc-500">{job.scheduled_date} {job.scheduled_time_start && `at ${job.scheduled_time_start}`} {job.address && <>- {job.address}<a href={mapsUrl(job.address)} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} className="ml-1 inline-flex align-middle text-zinc-500 hover:text-emerald-700" data-testid={`dashboard-schedule-map-link-${job.id}`} aria-label="Open scheduled job address in Google Maps">↗</a></>}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">tentative</Badge>
                </button>
              ))}
              {schedulingQueue.length > 5 && (
                <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => navigate('/jobs')}>
                  View all {schedulingQueue.length} tentative jobs
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <Card className="rounded-[1.5rem] border border-blue-100 shadow-sm sm:rounded-md lg:col-span-2" data-testid="todays-schedule-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-xl font-black font-heading sm:text-lg sm:font-bold">
              <Clock className="w-5 h-5 text-blue-600" /> Today&apos;s Schedule
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="todays-schedule">
            {stats.todays_job_list?.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4 text-center">No jobs scheduled for today</p>
            ) : (
              <div className="space-y-2">
                {stats.todays_job_list?.map((job) => (
                  <div
                    key={job.id}
                    className="flex flex-col gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 transition-colors cursor-pointer hover:bg-blue-100 sm:flex-row sm:items-center sm:justify-between sm:rounded-md sm:bg-white sm:p-3"
                    onClick={() => navigate('/jobs')}
                    data-testid={`dashboard-job-${job.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-1 h-10 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getDBAColor(job.dba_id) }}
                      />
                      <div className="min-w-0">
                        <p className="text-lg font-black leading-tight text-zinc-900 sm:truncate sm:text-sm sm:font-semibold">{job.title}</p>
                        <p className="text-sm font-semibold text-zinc-600 sm:text-xs sm:font-normal sm:text-zinc-500">
                          {job.scheduled_time_start && `${job.scheduled_time_start} - ${job.scheduled_time_end}`}
                          {job.address && <> | {job.address}<a href={mapsUrl(job.address)} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} className="ml-1 inline-flex align-middle text-zinc-500 hover:text-emerald-700" data-testid={`dashboard-recent-job-map-link-${job.id}`} aria-label="Open recent job address in Google Maps">↗</a></>}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className={`text-xs ${statusColors[job.status] || ''}`}>
                      {job.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[1.5rem] border border-emerald-100 shadow-sm sm:rounded-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-xl font-black font-heading sm:text-lg sm:font-bold">
              <Activity className="w-5 h-5 text-emerald-600" /> Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="recent-activity">
            {stats.recent_activity?.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4 text-center">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_activity?.slice(0, 8).map((act, i) => (
                  <div key={act.id || i} className="flex gap-3 rounded-2xl bg-emerald-50 p-3 sm:bg-transparent sm:p-0">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-zinc-800 sm:font-normal sm:text-zinc-700">{act.details}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {act.created_at && new Date(act.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}

function MiniCommandMetric({ label, value, tone }) {
  const colors = {
    blue: 'border-blue-100 bg-blue-600 text-white',
    orange: 'border-orange-100 bg-orange-500 text-white',
  };
  return <div className={`rounded-[1.5rem] border p-4 shadow-sm sm:rounded-md ${colors[tone] || colors.blue}`}><p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-white/80">{label}</p><p className="font-heading text-3xl font-black">{value}</p></div>;
}
